import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, RotateCcw, FlaskConical } from 'lucide-react';
import { Panel, Button, SinDatos, DataState } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { useForecast, useSettings, useRecommendations } from '@/lib/hooks';
import { api } from '@/lib/api';
import { PASOS, RESET, type Contexto, type Medicion } from './pasos';
import { cn } from '@/lib/cn';

/**
 * Recorrido guiado: el producto explicado moviendo sus propias palancas.
 *
 * ## Por qué existe
 *
 * Una herramienta de compras no se entiende leyendo una pantalla llena de números: se
 * entiende **viendo qué pasa cuando cambia un supuesto**. Acá cada paso mueve una palanca
 * de verdad —el tiempo de entrega, el nivel de servicio, la cobertura—, vuelve a correr el
 * motor y **escribe la medición**. Al final queda un registro de todo lo que el motor hizo
 * adelante de quien mira.
 *
 * ## Las dos decisiones de diseño que importan
 *
 * 1. **Se dice lo que se espera ANTES de apretar.** Cada paso muestra su premisa y su
 *    resultado esperado; recién después aparece el medido. Así el recorrido es una prueba y
 *    no una demostración: si el motor no hace lo que el texto dice, se ve al instante.
 *
 * 2. **Nada está simulado.** Se llama a la API real y el resultado se vuelve a LEER de la
 *    base. Números escritos a mano harían de esto una presentación, que es justo lo
 *    contrario de lo que hace falta.
 */
export default function TourPage() {
  const forecast = useForecast();
  const settings = useSettings();
  const recs = useRecommendations();

  const [hechos, setHechos] = useState<Record<string, Medicion[]>>({});
  const [ocCreada, setOcCreada] = useState<string | null>(null);
  const [corriendo, setCorriendo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const indiceActual = PASOS.findIndex((p) => !(p.id in hechos));
  const terminado = indiceActual === -1;

  /** Lee el estado real: es el "antes" de un paso y el "después" del anterior. */
  async function leerContexto(): Promise<Contexto> {
    const [f, s] = await Promise.all([api.forecast(), api.settings()]);
    const porSku: Contexto['porSku'] = {};
    for (const item of f.items) porSku[item.sku] = item;
    return { porSku, settings: { serviceLevel: s.serviceLevel, coverageTargetDays: s.coverageTargetDays } };
  }

  async function ejecutar(pasoId: string) {
    setError(null);
    setCorriendo(pasoId);
    try {
      const antes = await leerContexto();

      // La acción de cada paso. Vive acá y no en `pasos.ts` porque necesita la API y el
      // estado del componente; el paso define QUÉ se mide, esto define QUÉ se toca.
      if (pasoId === 'espera') {
        await api.updateProduct('DEMO-1', { leadTimeDays: 120 });
        await api.recalcularForecast();
      } else if (pasoId === 'servicio') {
        await api.updateSettings({ serviceLevel: 99 });
        await api.recalcularForecast();
      } else if (pasoId === 'cobertura') {
        await api.updateSettings({ coverageTargetDays: 60 });
        await api.recalcularForecast();
      } else if (pasoId === 'comprar') {
        const lista = await api.recommendations();
        const objetivo = lista.items.find((r) => r.sku === 'DEMO-2') ?? lista.items[0];
        if (!objetivo) throw new Error('No hay ninguna recomendación para aprobar.');
        const res = await api.approveRecommendation(objetivo.id);
        setOcCreada((res as { oc?: string })?.oc ?? null);
      }
      // El paso 'leer' no toca nada a propósito: sólo muestra de dónde sale el número.

      const despues = await leerContexto();
      const paso = PASOS.find((p) => p.id === pasoId)!;
      setHechos((h) => ({ ...h, [pasoId]: paso.medir(antes, despues) }));
      await Promise.all([forecast.refetch(), settings.refetch(), recs.refetch()]);
    } catch (e) {
      setError((e as Error).message || 'No se pudo completar el paso.');
    } finally {
      setCorriendo(null);
    }
  }

  async function reiniciar() {
    setError(null);
    setCorriendo('reset');
    try {
      await api.updateProduct('DEMO-1', { leadTimeDays: RESET.leadTimeDays });
      await api.updateSettings({
        serviceLevel: RESET.serviceLevel,
        coverageTargetDays: RESET.coverageTargetDays,
      });
      await api.recalcularForecast();
      setHechos({});
      setOcCreada(null);
      await Promise.all([forecast.refetch(), settings.refetch(), recs.refetch()]);
    } catch (e) {
      setError((e as Error).message || 'No se pudo reiniciar.');
    } finally {
      setCorriendo(null);
    }
  }

  return (
    <DataState query={forecast}>
      {(f) => {
        // El recorrido está escrito sobre los dos productos de ejemplo. Con otro catálogo
        // los pasos no tendrían nada que mover, y decirlo es mejor que fallar a mitad.
        const hayDemo = f.items.some((i) => i.sku === 'DEMO-1') && f.items.some((i) => i.sku === 'DEMO-2');
        if (!hayDemo) {
          return (
            <div className="space-y-6">
              <PageHeader title="Recorrido guiado" subtitle="El producto explicado moviendo sus propias palancas" />
              <SinDatos
                title="El recorrido necesita los dos productos de ejemplo"
                detail="Está escrito sobre DEMO-1 y DEMO-2, que son dos casos con los números elegidos para poder seguirlos con una calculadora."
                comoLlenarlo="Cerrá todo y corré «Probar Forecast (2 productos).bat». Después volvé acá."
              />
            </div>
          );
        }

        return (
          <div className="space-y-6">
            <PageHeader
              title="Recorrido guiado"
              subtitle="Cada paso mueve una palanca de verdad y mide qué cambió"
              actions={
                Object.keys(hechos).length > 0 ? (
                  <Button
                    variant="outline"
                    onClick={reiniciar}
                    disabled={corriendo !== null}
                    icon={<RotateCcw className="h-4 w-4" />}
                  >
                    {corriendo === 'reset' ? 'Reiniciando…' : 'Empezar de nuevo'}
                  </Button>
                ) : undefined
              }
            />

            {error && (
              <div className="surface border-l-2 border-l-danger p-4 text-xs text-danger-text">{error}</div>
            )}

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
              {/* ── Los pasos ─────────────────────────────────────────────── */}
              <div className="space-y-3">
                {PASOS.map((paso, i) => {
                  const hecho = paso.id in hechos;
                  const actual = i === indiceActual;
                  const futuro = !hecho && !actual;
                  return (
                    <div
                      key={paso.id}
                      // Entrada escalonada: cada paso entra 60 ms después del anterior. Es
                      // lo que hace que la lista se lea como una secuencia y no como un bloque.
                      className={cn(
                        'surface animate-fade-in p-4 transition-[opacity,box-shadow] duration-200',
                        actual && 'shadow-glow',
                        futuro && 'opacity-45',
                      )}
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={cn(
                            // Radio concéntrico: la tarjeta es 18px con 16px de padding, así
                            // que lo de adentro va en 10px.
                            'mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-[10px] text-xs font-bold tabular-nums',
                            hecho && 'bg-success-soft text-success-text',
                            actual && 'bg-primary text-white',
                            futuro && 'bg-card-2 text-ink-faint',
                          )}
                        >
                          {hecho ? <Check className="h-4 w-4" strokeWidth={3} /> : i + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-ink-strong" style={{ textWrap: 'balance' }}>
                            {paso.titulo}
                          </p>

                          {(actual || hecho) && (
                            <p className="mt-1.5 text-xs leading-relaxed text-ink-muted" style={{ textWrap: 'pretty' }}>
                              {paso.premisa}
                            </p>
                          )}

                          {actual && (
                            <>
                              {/* Lo esperado se dice ANTES de apretar: eso convierte el paso
                                  en una prueba en vez de en una demostración. */}
                              <p className="mt-3 rounded-[10px] bg-card-2 p-3 text-2xs leading-relaxed text-ink-faint">
                                <span className="font-semibold uppercase tracking-wide text-ink-muted">
                                  Qué debería pasar
                                </span>
                                <br />
                                {paso.esperado}
                              </p>
                              <Button
                                variant="primary"
                                className="mt-3 w-full"
                                onClick={() => ejecutar(paso.id)}
                                disabled={corriendo !== null}
                                icon={<ArrowRight className="h-4 w-4" />}
                              >
                                {corriendo === paso.id ? 'Midiendo…' : paso.accion}
                              </Button>
                            </>
                          )}

                          {hecho && <Mediciones filas={hechos[paso.id]!} />}
                          {hecho && paso.id === 'comprar' && ocCreada && (
                            <p className="mt-2 text-2xs text-ink-muted">
                              Se creó{' '}
                              <span className="text-ink-strong">{ocCreada}</span>.{' '}
                              <Link to="/ordenes" className="font-semibold text-primary-text hover:underline">
                                Verla en Órdenes de Compra →
                              </Link>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {terminado && (
                  <div className="surface animate-fade-in border-l-2 border-l-success p-4">
                    <p className="text-sm font-semibold text-ink-strong">Eso es el producto entero.</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-ink-muted" style={{ textWrap: 'pretty' }}>
                      Proyectar la demanda, traducir el tiempo del proveedor en una fecha, decidir cuánto
                      colchón vale la pena, y dejar la compra registrada. Todo lo que viste salió del motor
                      con los datos que hay en la base — nada estaba escrito de antemano.
                    </p>
                  </div>
                )}
              </div>

              {/* ── La mesa: estado vivo + registro ───────────────────────── */}
              <div className="space-y-4">
                <Panel>
                  <div className="flex items-center gap-2 border-b border-hairline p-4">
                    <FlaskConical className="h-4 w-4 text-info-text" />
                    <p className="text-sm font-semibold text-ink-strong">Los dos productos, ahora</p>
                  </div>
                  <div className="grid grid-cols-1 gap-px bg-hairline sm:grid-cols-2">
                    {['DEMO-1', 'DEMO-2'].map((sku) => {
                      const it = f.items.find((x) => x.sku === sku);
                      return <FichaProducto key={sku} sku={sku} item={it} />;
                    })}
                  </div>
                  <div className="flex items-center justify-between gap-4 border-t border-hairline p-4 text-2xs text-ink-faint">
                    <span>
                      Nivel de servicio{' '}
                      <b className="tabular-nums text-ink-strong">{settings.data?.serviceLevel ?? '—'} %</b>
                    </span>
                    <span>
                      Cobertura objetivo{' '}
                      <b className="tabular-nums text-ink-strong">
                        {settings.data?.coverageTargetDays ?? '—'} días
                      </b>
                    </span>
                  </div>
                </Panel>

                <Panel>
                  <div className="border-b border-hairline p-4">
                    <p className="text-sm font-semibold text-ink-strong">Registro de mediciones</p>
                    <p className="mt-0.5 text-2xs text-ink-faint">
                      Cada línea salió de volver a leer la base después de la acción
                    </p>
                  </div>
                  {Object.keys(hechos).length === 0 ? (
                    <p className="p-6 text-center text-xs text-ink-faint">
                      Todavía no se midió nada. Empezá por el paso 1.
                    </p>
                  ) : (
                    <div className="divide-y divide-hairline">
                      {PASOS.filter((p) => p.id in hechos && hechos[p.id]!.length > 0).map((p) => (
                        <div key={p.id} className="p-4">
                          <p className="text-2xs font-semibold uppercase tracking-wide text-ink-faint">{p.titulo}</p>
                          <div className="mt-2 space-y-1.5">
                            {hechos[p.id]!.map((m) => (
                              <FilaMedicion key={m.concepto} m={m} />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Panel>
              </div>
            </div>
          </div>
        );
      }}
    </DataState>
  );
}

/** El estado actual de un producto. Los números llevan `tabular-nums` para que no
 *  salten de ancho cuando cambian — que es justo lo que este recorrido los hace hacer. */
function FichaProducto({ sku, item }: { sku: string; item?: { demandaDiaria: number; stockSeguridad: number; puntoReorden: number; cantidadSugerida: number; leadTime?: number } }) {
  const filas: [string, string][] = [
    ['Demanda', item ? `${item.demandaDiaria} /día` : '—'],
    ['Colchón', item ? `${item.stockSeguridad} uds` : '—'],
    ['Punto de reorden', item ? `${item.puntoReorden} uds` : '—'],
    ['Comprar', item ? `${item.cantidadSugerida} uds` : '—'],
  ];
  return (
    <div className="bg-panel p-4">
      <p className="font-mono text-xs font-semibold text-ink-strong">{sku}</p>
      <dl className="mt-3 space-y-2">
        {filas.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-3">
            <dt className="text-2xs text-ink-faint">{k}</dt>
            <dd className="text-sm font-semibold tabular-nums text-ink-strong transition-colors duration-500">
              {v}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Mediciones({ filas }: { filas: Medicion[] }) {
  if (!filas.length) return null;
  return (
    <div className="mt-3 space-y-1.5 rounded-[10px] bg-card-2 p-3">
      {filas.map((m) => (
        <FilaMedicion key={m.concepto} m={m} />
      ))}
    </div>
  );
}

/** `antes → después`, con el delta sólo si se movió. Un "0 %" al lado de un número que no
 *  cambió se lee como una medición cuando en realidad no hay nada que reportar. */
function FilaMedicion({ m }: { m: Medicion }) {
  const cambio = m.despues !== m.antes;
  const fmt = (n: number) =>
    `${Number.isInteger(n) ? n.toLocaleString('es-CO') : n.toFixed(2).replace('.', ',')}${m.sufijo ?? ''}`;
  return (
    <div className="flex items-baseline justify-between gap-3 text-2xs">
      <span className="min-w-0 flex-1 truncate text-ink-muted">{m.concepto}</span>
      <span className="shrink-0 tabular-nums">
        <span className={cn(cambio ? 'text-ink-faint line-through' : 'text-ink-muted')}>{fmt(m.antes)}</span>
        {cambio && (
          <>
            <span className="mx-1.5 text-ink-ghost">→</span>
            <span className="font-semibold text-success-text">{fmt(m.despues)}</span>
          </>
        )}
        {!cambio && <span className="ml-1.5 text-ink-ghost">(sin cambio)</span>}
      </span>
    </div>
  );
}
